import React from 'react';
import { Award, Briefcase, GraduationCap } from 'lucide-react';

export const About: React.FC = () => {
  return (
    <section id="about" className="py-20 opacity-0 transition-opacity duration-1000">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ink">About Sudipta Dhara - Software Developer & Generative AI Engineer</h2>
          <div className="w-24 h-1 bg-brand mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="glass p-8 rounded-lgx mb-8">
              <p className="text-xs text-ink-dim mb-4 text-right">Last updated: December 2025</p>
              <p className="text-lg text-ink mb-4">
                I'm Sudipta Dhara, a passionate Software Developer and Generative AI Engineer with over 14 years of experience 
                in building robust applications, scalable backend systems, and cutting-edge AI solutions. Based in Kolkata, 
                West Bengal, India, I specialize in backend development, generative AI, and creating innovative technology 
                products that solve real-world problems.
              </p>
              <p className="text-lg text-ink-dim mb-4">
                As a Lead Generative AI Developer at DECA Games, I'm building production-ready RAG pipelines, agentic AI 
                systems, and LLM applications using LangChain, LangGraph, vector databases, and modern observability tooling. 
                My expertise spans Python, PHP, Django, Node.js, and cloud technologies, enabling me to deliver scalable 
                solutions for complex technical challenges.
              </p>
              <p className="text-lg text-ink-dim mb-4">
                Beyond my role at DECA Games, I'm the creator of 7 innovative tech products including Talk2Term (AI-powered 
                terminal automation), MegaSearch (intelligent metasearch engine), ProDev's News (AI news aggregator), 
                MeetEcho (transcription platform), RitualRhythms (society management), MySmartNest (smart home automation), 
                and ProDevs (technology platform). Each product demonstrates my commitment to solving real problems with 
                elegant, user-focused solutions.
              </p>
              <p className="text-lg text-ink-dim">
                My current focus is on Generative AI — building robust RAG pipelines, agentic systems, and production LLM 
                applications using LangChain, LangGraph, vector databases, and observability tooling. I'm passionate about 
                exploring new technologies, contributing to the developer community, and always open to new challenges that 
                push the boundaries of what's possible with AI and software development. Explore my <a href="#products" className="text-brand hover:underline">products and startups</a> or view my <a href="#experience" className="text-brand hover:underline">work experience</a> to learn more.
              </p>
            </div>
            
            <div className="glass p-8 rounded-lgx mb-8">
              <h3 className="text-2xl font-bold text-ink mb-4">Why Choose Me?</h3>
              <ul className="space-y-3 text-ink-dim">
                <li className="flex items-start gap-3">
                  <span className="text-brand mt-1">▸</span>
                  <span><strong className="text-ink">14+ Years of Experience:</strong> Proven track record in software development, 
                  backend systems, and AI applications across multiple industries including gaming, SaaS, and enterprise solutions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand mt-1">▸</span>
                  <span><strong className="text-ink">Generative AI Expertise:</strong> Deep knowledge in RAG pipelines, agentic AI, 
                  LangChain, LangGraph, and production LLM applications with hands-on experience building scalable AI systems.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand mt-1">▸</span>
                  <span><strong className="text-ink">Product Portfolio:</strong> Created 7 active products serving thousands of users, 
                  demonstrating ability to identify market needs and deliver complete solutions from concept to production.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand mt-1">▸</span>
                  <span><strong className="text-ink">Full-Stack Capability:</strong> Expertise in both front-end and back-end technologies, 
                  enabling end-to-end development of complex applications with modern frameworks and best practices.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand mt-1">▸</span>
                  <span><strong className="text-ink">Leadership Experience:</strong> Leading AI development initiatives across entire 
                  organizations, mentoring teams, and establishing best practices for generative AI technologies.</span>
                </li>
              </ul>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="glass p-6 rounded-lgx text-center">
                <Briefcase size={32} className="text-brand mx-auto mb-3" />
                <h3 className="font-bold text-ink mb-1">Experience</h3>
                <p className="text-ink-dim">14+ Years</p>
              </div>
              <div className="glass p-6 rounded-lgx text-center">
                <GraduationCap size={32} className="text-brand mx-auto mb-3" />
                <h3 className="font-bold text-ink mb-1">Education</h3>
                <p className="text-ink-dim">Master's Degree</p>
              </div>
              <div className="glass p-6 rounded-lgx text-center">
                <Award size={32} className="text-brand mx-auto mb-3" />
                <h3 className="font-bold text-ink mb-1">Projects</h3>
                <p className="text-ink-dim">100+ Completed</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-ink mb-6">Professional Summary</h3>
            
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-1 bg-brand flex-shrink-0"></div>
                <div>
                  <h4 className="font-bold text-ink">Technical Expertise</h4>
                  <p className="text-ink-dim">Proficient in various programming languages and frameworks, with a focus on creating efficient and scalable solutions.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-1 bg-brand flex-shrink-0"></div>
                <div>
                  <h4 className="font-bold text-ink">Leadership</h4>
                  <p className="text-ink-dim">Experienced in leading development teams and managing complex projects from inception to delivery.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-1 bg-brand flex-shrink-0"></div>
                <div>
                  <h4 className="font-bold text-ink">Problem Solving</h4>
                  <p className="text-ink-dim">Strong analytical skills with a proven track record of identifying issues and implementing effective solutions.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-1 bg-brand flex-shrink-0"></div>
                <div>
                  <h4 className="font-bold text-ink">Communication</h4>
                  <p className="text-ink-dim">Excellent verbal and written communication skills, facilitating effective collaboration with stakeholders at all levels.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
